import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormGroup, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { DroitContrainteDto, ListeEmetteurDto, ListeNatureDroitDto } from 'src/app/shared/models/droit-contraint.dto';
import { ValeurDomaineDto } from 'src/app/shared/models/domaines.dto';
import { RechercheRdeService } from 'src/app/shared/services/recherche-rde.service';
import { DomainesService } from 'src/app/shared/services/domaines.service';
import { Constantes } from 'src/app/shared/constantes';
import { MessageService } from '@igo2/core/message';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { CommonModule } from '@angular/common';
import { EntityRecord, EntityTableButton, EntityTableColumnRenderer, EntityTableTemplate } from '@igo2/common/entity';
import { LanguageService } from '@igo2/core/language';
import * as olstyle from 'ol/style';
import { Feature } from '@igo2/geo';
import { Point } from 'ol/geom';
import { fromLonLat } from 'ol/proj';

@Component({
    selector: 'app-droits-contraintes',
    templateUrl: './droits-contraintes.component.html',
    styleUrls: ['./droits-contraintes.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        TranslateModule,
        MatInputModule,
        MatFormFieldModule,
        MatSelectModule,
        MatGridListModule,
        MatDatepickerModule,
        MatButtonModule
    ]
})
export class DroitsContraintesComponent implements OnInit {
  template: EntityTableTemplate; 
  templateHistory: EntityTableTemplate; 
  style: olstyle.Style;

  form: UntypedFormGroup;
  messagesDeValidation: { [key: string]: string } = {};
  statuts: ValeurDomaineDto[];
  dataSourceEmetteur: ListeEmetteurDto[];
  dataSourceNatureDroit: ListeNatureDroitDto[];
  valeurDateDefaut: string = 'enregModif';
  layerid: string = 'RDEDroitsContaintes';
  historyLayerid: string = 'RDEDroitsContraintesHistorique';
  buttonClicked:boolean  = false;

  constructor(
    private rechercheRdeService: RechercheRdeService,
    private formBuilder: UntypedFormBuilder,
    private messageService: MessageService,
    private languageService: LanguageService,
    private domainesService: DomainesService,
    private cdRef: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
      const actionHandlers: Record<string,(row: any, record?: EntityRecord<object>) => void > = {
      details: (row, record) => {
        if (record?.state?.selected) {
          this.rechercheRdeService.obtenirFicheDescriptive(row).subscribe({
            next: (blob: Blob) => {
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = 'Fiche - Droits et contraintes.pdf';
              a.click();
              window.URL.revokeObjectURL(url); // Clean up
            },
            error: (err) => {
              console.error('Erreur lors du téléchargement du PDF', err);
            }
          });
        }
      },

      getHistoricalData: (row, record) => {
        if (record?.state?.selected) {
          this.rechercheRdeService.obtenirHistorique(row).subscribe({
            next: (history:DroitContrainteDto[]) => {
            if(history.length > 0 ){
              this.rechercheRdeService.addSearchResultsToMap(
                  this.historyLayerid, 
                  this.rechercheRdeService.formatFeaturesForTable('droitContraintes', history, true),
                  this.cdRef,
                  this.templateHistory,
                  this.style
                )
              } else {
                this.messageService.info(this.messagesDeValidation.listeVide);
              }
            },
            error: (err) => {
              console.error('Erreur lors de la reception historique', err);
            }
          });
        }
      }
    }

    this.template = this.rechercheRdeService.getEntityTableTemplate('droitContraintes',actionHandlers);

    const historyActionHandlers: Record<string,(row: any, record?: EntityRecord<object>) => void > = {
      details: (row, record) => {
        if (record?.state?.selected) {
          this.rechercheRdeService.obtenirFicheDescriptive(row, true).subscribe({
            next: (blob: Blob) => {
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = 'Fiche - Droits et contraintes.pdf';
              a.click();
              window.URL.revokeObjectURL(url); // Clean up
            },
            error: (err) => {
              console.error('Erreur lors du téléchargement du PDF', err);
            }
          });
        }
      }
    };
    this.templateHistory = this.rechercheRdeService.getEntityTableTemplate('droitContraintes', historyActionHandlers);

    this.form = this.formBuilder.group({
      dateDebut: [""],
      dateFin: [""],
      emetteur: [""],
      natureDroit: [""],
      numeroDroitContrainte: [""],
      date: [this.valeurDateDefaut],
      statut: ["", [Validators.required]]
    },
      {
        validators: [this.validerDateDebutFin.bind(this)]
      }
    );

  this.form.valueChanges.subscribe(() => {
    if (this.form.valid) {
      this.buttonClicked = false;
    }
  });

    this.domainesService.obtenirValeurs("STATUT_EMETTEUR_RDE").subscribe(reponse => {
      if (reponse) {
        this.statuts = reponse.valeursDomaine;
      }
    });

    this.messagesDeValidation.dateDebutSuperieurFin = Constantes.MessageApplicatif.DateFinSuperieurDateDebut_15E;
    this.messagesDeValidation.listeVide = Constantes.MessageApplicatif.AucunElement_6I;
    this.messagesDeValidation.messageTechnique = Constantes.MessageApplicatif.CoucheIndisponible_16T;
    this.messagesDeValidation.grilleVide = Constantes.MessageApplicatif.AucunElement_6I;
    this.messagesDeValidation.erreurCouche = Constantes.MessageApplicatif.CoucheIndisponible_16T;
    this.messagesDeValidation.obligatoireNumero = Constantes.MessageApplicatif.ValeurObligatoire_1E
      .replace("{0}", this.getCleanKey('droitContraintes.numero'));    
 
  }

  chargerListeEmetteur(statut: string) {
    this.rechercheRdeService.obtenirListeEmetteur(statut).subscribe(reponse => {
      this.dataSourceEmetteur = reponse;
      this.chargerNatureDroit([]);
    });
  }

  validerDateDebutFin(formGroup: FormGroup): Validators {
    if (formGroup.value.dateFin && formGroup.value.dateDebut) {
      const dateDebut = this.form.get("dateDebut").value;
      const dateFin = this.form.get("dateFin").value;
      if (dateDebut > dateFin) {
        this.messageService.error(this.messagesDeValidation.dateDebutSuperieurFin
          .replace('{0}', ""));
        return { notValid: true };
      }
    }
    if (formGroup.value.dateDebut) {
      return Validators.required(formGroup.controls["dateFin"]);
    }
    if (formGroup.value.dateFin) {
      return Validators.required(formGroup.controls["dateDebut"]);
    }

    if (formGroup.value.date || formGroup.value.statut) {
      const date = formGroup.controls["date"].value;
      const statut = formGroup.controls["statut"].value;

      if ((date ===null || date?.length === 0) || (statut ===null || statut?.length === 0)){
        return { notValid: true };
      }

      return null;
    }

    return { notValid: true };
  }

  chargerNatureDroit(emetteurs: string[]) {
    const statut = this.form.get("statut").value;
    if (statut) {
      this.rechercheRdeService.obtenirListeNatureDroit(statut, emetteurs).subscribe(reponse => {
        this.dataSourceNatureDroit = reponse;
      });
    }
  }

  onRechercher() {
    this.buttonClicked = true;
    this.rechercher();
  }

  rechercher() {
    const numeroDroitContrainte = this.form.get("numeroDroitContrainte").value;
    const statut = this.form.get("statut").value;
    const emetteurs = this.form.get("emetteur").value;
    const naturesDroit = this.form.get("natureDroit").value;
    const dateFin = this.form.get("dateFin").value;
    const dateDebut = this.form.get("dateDebut").value;
    const typeDate = this.form.get("date").value;
    const indicateurConfidentiel = false;
    const indicateurHistorique = false;
    
    this.rechercheRdeService.obtenirListeDroitsContraintes( 
      statut, 
      indicateurHistorique, 
      indicateurConfidentiel,
      numeroDroitContrainte,
      emetteurs, 
      naturesDroit, 
      typeDate, 
      dateDebut, 
      dateFin)
    .subscribe({
      next: reponse => {
        if (reponse?.length > 0){
          this.rechercheRdeService.addSearchResultsToMap(
            this.layerid, 
            this.rechercheRdeService.formatFeaturesForTable('droitContraintes', reponse),
            this.cdRef,
            this.template,
            this.style
          )
        } else {
          this.messageService.info(this.messagesDeValidation.listeVide);
        }
      },
      error: error => {
        this.messageService.error(this.messagesDeValidation.erreurCouche);
        console.log(error);
      }
    });
  }

  initialiserChamps(){
    this.form.patchValue({
      dateDebut: '',
      dateFin: '',
      emetteur: '',
      natureDroit: '',
      numeroDroitContrainte: '',
      date: this.valeurDateDefaut,
      statut: ''
    });
    this.form.get('statut').setErrors(null,{emitEvent: false});
    this.form.get('date').setErrors(null,{emitEvent: false});
  }

  getCleanKey(key: string) {
        return this.languageService.translate.instant(`sigt.integration.rechercheRde.form.${key}`)
            .replace("'", "’")
            .replace(".", "");
    }

}